"use client";

import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { reorderEntities } from "@/app/services/server/actions/reorder-entities";
import type { BackendTableRowT } from "@/app/types/backend-table";

/**
 * Hook for drag & drop table functionality
 *
 * Manages drag & drop state, sorting logic, and server synchronization.
 * Handles automatic sort_index calculation and debounced server updates.
 *
 * @param data - Array of table rows
 * @param tableName - Database table name for server action
 * @param onReorderComplete - Callback after successful reorder
 * @returns Object with drag & drop state and handlers
 */
export function useDragDropTable(
  data: BackendTableRowT[],
  tableName: string,
  onReorderComplete?: () => void
) {
  const [items, setItems] = useState<BackendTableRowT[]>(data);
  const [isReordering, setIsReordering] = useState(false);
  const lastSuccessfulOrderRef = useRef<BackendTableRowT[]>(data);
  const pendingServerOrderRef = useRef<BackendTableRowT[] | null>(null);
  const reorderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applySortIndex = useCallback(
    (
      records: BackendTableRowT[],
      pinnedIds: Set<string>
    ): BackendTableRowT[] => {
      let nextIndex = 1;

      // If no pinned items, assign sort_index to all items
      if (pinnedIds.size === 0) {
        return records.map((item) => ({
          ...item,
          sort_index: nextIndex++,
        }));
      }

      return records.map((item) => {
        if (pinnedIds.has(item.id)) {
          return {
            ...item,
            sort_index: nextIndex++,
          };
        }

        if (item.sort_index === null || item.sort_index === undefined) {
          return item;
        }

        return {
          ...item,
          sort_index: null,
        };
      });
    },
    []
  );

  const haveSameOrder = useCallback(
    (first: BackendTableRowT[], second: BackendTableRowT[]) => {
      if (first.length !== second.length) {
        return false;
      }

      return first.every((item, index) => item.id === second[index]?.id);
    },
    []
  );

  const haveSameSnapshot = useCallback(
    (first: BackendTableRowT[], second: BackendTableRowT[]) => {
      if (first.length !== second.length) {
        return false;
      }

      return first.every((item, index) => {
        const comparator = second[index];

        if (!comparator || item.id !== comparator.id) {
          return false;
        }

        const currentSortIndex = item.sort_index ?? null;
        const nextSortIndex = comparator.sort_index ?? null;

        if (currentSortIndex !== nextSortIndex) {
          return false;
        }

        const currentCreatedAt = item.createdAt ?? "";
        const nextCreatedAt = comparator.createdAt ?? "";
        if (currentCreatedAt !== nextCreatedAt) {
          return false;
        }

        const currentUpdatedAt = item.updatedAt ?? "";
        const nextUpdatedAt = comparator.updatedAt ?? "";
        if (currentUpdatedAt !== nextUpdatedAt) {
          return false;
        }

        return true;
      });
    },
    []
  );

  const sortedItems = useMemo(() => {
    const withSortIndex = items
      .filter(
        (item): item is BackendTableRowT & { sort_index: number } =>
          typeof item.sort_index === "number"
      )
      .sort((a, b) => (a.sort_index as number) - (b.sort_index as number));

    const withoutSortIndex = items
      .filter(
        (item) => item.sort_index === null || item.sort_index === undefined
      )
      .sort((a, b) => {
        const aDate = new Date(a.createdAt || a.updatedAt || 0).getTime();
        const bDate = new Date(b.createdAt || b.updatedAt || 0).getTime();
        return bDate - aDate;
      });

    if (withSortIndex.length === 0) {
      return withoutSortIndex;
    }

    return [...withSortIndex, ...withoutSortIndex];
  }, [items]);

  // Update items when data changes (e.g., from server refresh)
  // Only update if we don't have conflicting local changes to avoid overriding user actions
  useEffect(() => {
    if (isReordering) {
      return;
    }

    const pendingOrder = pendingServerOrderRef.current;
    if (pendingOrder) {
      if (!haveSameOrder(data, pendingOrder)) {
        // Ignore stale server payloads while we wait for the updated order
        return;
      }

      pendingServerOrderRef.current = null;
      lastSuccessfulOrderRef.current = pendingOrder;
    }

    if (!haveSameSnapshot(items, data)) {
      setItems(data);
      lastSuccessfulOrderRef.current = data;
    } else if (!pendingOrder) {
      lastSuccessfulOrderRef.current = data;
    }
  }, [data, haveSameOrder, haveSameSnapshot, isReordering, items]);

  useEffect(() => {
    return () => {
      if (reorderTimeoutRef.current) {
        clearTimeout(reorderTimeoutRef.current);
      }
    };
  }, []);

  // Configure sensors for drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Debounced server update with optimistic updates
  const updateServerOrder = useCallback(
    async (newItems: BackendTableRowT[]) => {
      if (haveSameSnapshot(lastSuccessfulOrderRef.current, newItems)) {
        pendingServerOrderRef.current = null;
        onReorderComplete?.();
        return;
      }

      try {
        setIsReordering(true);

        // Calculate new sort_index values (only for pinned items)
        const updates = newItems
          .filter(
            (item): item is BackendTableRowT & { sort_index: number } =>
              typeof item.sort_index === "number"
          )
          .map((item) => ({
            id: item.id,
            sort_index: item.sort_index,
          }));

        if (updates.length === 0) {
          pendingServerOrderRef.current = null;
          lastSuccessfulOrderRef.current = newItems;
          onReorderComplete?.();
          return;
        }

        await reorderEntities(tableName, updates);

        // Update successful state - keep the current order
        lastSuccessfulOrderRef.current = newItems;
        onReorderComplete?.();
      } catch (error) {
        console.error("Error updating server order:", error);
        // Revert to last successful state on error
        setItems(lastSuccessfulOrderRef.current);
        pendingServerOrderRef.current = null;
      } finally {
        setIsReordering(false);
      }
    },
    [haveSameSnapshot, onReorderComplete, tableName]
  );

  // Handle drag end with optimistic updates
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = sortedItems.findIndex((item) => item.id === active.id);
      const newIndex = sortedItems.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const currentPinnedIds = new Set(
          sortedItems
            .filter(
              (item): item is BackendTableRowT & { sort_index: number } =>
                typeof item.sort_index === "number"
            )
            .map((item) => item.id)
        );

        const activeWasPinned =
          typeof sortedItems[oldIndex]?.sort_index === "number";

        const shouldPinActive =
          activeWasPinned ||
          currentPinnedIds.size === 0 ||
          newIndex < currentPinnedIds.size;

        const activeId = String(active.id);

        if (shouldPinActive) {
          currentPinnedIds.add(activeId);
        } else {
          currentPinnedIds.delete(activeId);
        }

        const reorderedItems = arrayMove(sortedItems, oldIndex, newIndex);
        const newItems = applySortIndex(reorderedItems, currentPinnedIds);

        // Optimistic update: UI changes immediately
        setItems(newItems);
        pendingServerOrderRef.current = newItems;

        if (reorderTimeoutRef.current) {
          clearTimeout(reorderTimeoutRef.current);
        }

        // Debounced server update (500ms)
        reorderTimeoutRef.current = setTimeout(() => {
          updateServerOrder(newItems);
        }, 500);
      }
    },
    [sortedItems, updateServerOrder, applySortIndex]
  );

  return {
    items: sortedItems,
    isReordering,
    sensors,
    handleDragEnd,
    DndContext,
    SortableContext,
    verticalListSortingStrategy,
    closestCenter,
  };
}
